/**
 * Lógica pura de los honorarios de la fiduciaria (Mati, 2026-10-20; registro de fees, 2026-11-02): el registro de los
 * fees generados (filtros, totales y la selección de los A por cobrar para liquidarlos), quiénes reciben el fee B y el
 * formulario de la liquidación. Mismos textos de error que el backend (`app/honorarios/service.py`).
 * Porcentajes escalados a 10^4 como en Participación: nunca float; montos en centavos `BigInt`.
 */
import type { BadgeTono, OpcionSelect } from "@/components";
import type { BeneficiarioFeeB, EstadoFee, FeeGenerado, Moneda, PayloadBeneficiarios, PayloadLiquidacion } from "./api";
import { filtrarOpciones } from "./buscar";
import { formatFecha, formatMoneda, hoyISO } from "./format";
import { TODOS, aCentavos, deCentavos, parseTipoCambio, tipoCambioAInput } from "./gastos";
import { CIEN, aEditable, aEscalado, aTextoApi, formatEscalado, partesIguales } from "./participacion";

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
export const MONEDAS: Moneda[] = ["ARS", "USD"];
export const LARGO_FACTURA = 40;

/** "2026-10" → "octubre 2026" (igual que el backend en el concepto de la caja). */
export function nombreMes(mes: string): string {
  const [anio, m] = mes.split("-");
  return `${MESES[Number(m) - 1]} ${anio}`;
}

export const NOMBRE_MONEDA: Record<Moneda, string> = { ARS: "pesos", USD: "dólares" };

// ------------------------------------------------------------------ registro de fees generados

export const ETIQUETA_ESTADO_FEE: Record<EstadoFee, string> = { por_cobrar: "Por cobrar", cobrado: "Cobrado", por_acreditar: "Por acreditar", acreditado: "Acreditado" };
export const TONO_ESTADO_FEE: Record<EstadoFee, BadgeTono> = { por_cobrar: "alerta", cobrado: "exito", por_acreditar: "alerta", acreditado: "exito" };

const conTodos = (etiqueta: string, lista: OpcionSelect[]): OpcionSelect[] => [{ valor: TODOS, etiqueta }, ...lista];
export const OPCIONES_FILTRO_TIPO_FEE = conTodos("A y B", [{ valor: "A", etiqueta: "A" }, { valor: "B", etiqueta: "B" }]);
export const OPCIONES_FILTRO_ESTADO_FEE = conTodos("Todos los estados", (Object.keys(ETIQUETA_ESTADO_FEE) as EstadoFee[]).map((e) => ({ valor: e, etiqueta: ETIQUETA_ESTADO_FEE[e] })));

export interface FiltrosRegistro {
  /** "AAAA-MM" o TODOS. */
  mes: string;
  tipo: string;
  estado: string;
  proveedorId: string;
  q: string;
}

export const filtrosRegistroIniciales = (): FiltrosRegistro => ({ mes: TODOS, tipo: TODOS, estado: TODOS, proveedorId: TODOS, q: "" });

/** Los meses con fees generados, el más nuevo primero. */
export function opcionesMesRegistro(items: FeeGenerado[]): OpcionSelect[] {
  const meses = [...new Set(items.map((i) => i.fecha.slice(0, 7)))].sort().reverse();
  return conTodos("Todos los meses", meses.map((m) => ({ valor: m, etiqueta: nombreMes(m) })));
}

export function opcionesProveedorRegistro(items: FeeGenerado[]): OpcionSelect[] {
  const proveedores = new Map<number, string>();
  for (const i of items) if (i.proveedor_id !== null && i.proveedor) proveedores.set(i.proveedor_id, i.proveedor);
  const lista = [...proveedores].sort((a, b) => a[1].localeCompare(b[1], "es")).map(([id, nombre]) => ({ valor: String(id), etiqueta: nombre }));
  return conTodos("Todos los proveedores", lista);
}

/** El buscador mira el gasto, el comprobante, el proveedor y las facturas de sus liquidaciones. */
export function filtrarRegistro(items: FeeGenerado[], f: FiltrosRegistro): FeeGenerado[] {
  const filtrados = items.filter(
    (i) =>
      (f.mes === TODOS || i.fecha.startsWith(f.mes)) &&
      (f.tipo === TODOS || i.tipo === f.tipo) &&
      (f.estado === TODOS || i.estado === f.estado) &&
      (f.proveedorId === TODOS || String(i.proveedor_id) === f.proveedorId),
  );
  const opciones = filtrados.map((i) => ({
    valor: String(i.fee_id), etiqueta: i.descripcion, busqueda: [i.comprobante_nro, i.proveedor, ...i.liquidaciones.map((l) => l.factura_nro)].filter(Boolean).join(" "), item: i,
  }));
  return filtrarOpciones(opciones, f.q).map((o) => o.item);
}

export interface TotalFees {
  /** Todo en USD, cada fee al tipo de cambio del pago que lo generó. */
  usd: string;
  ARS: string;
  USD: string;
}

function sumar(items: FeeGenerado[], monto: (i: FeeGenerado) => string, usd: (i: FeeGenerado) => string): TotalFees {
  const t = { usd: 0n, ARS: 0n, USD: 0n };
  for (const i of items) {
    t.usd += aCentavos(usd(i));
    t[i.moneda] += aCentavos(monto(i));
  }
  return { usd: deCentavos(t.usd), ARS: deCentavos(t.ARS), USD: deCentavos(t.USD) };
}

/** Las tres tarjetas, sobre lo filtrado: generado, cobrado (A) o acreditado (B) y por cobrar (o acreditar). */
export function totalesRegistro(items: FeeGenerado[]): { generado: TotalFees; cobrado: TotalFees; porCobrar: TotalFees } {
  return {
    generado: sumar(items, (i) => i.fee, (i) => i.fee_usd),
    cobrado: sumar(items, (i) => i.cobrado, (i) => i.cobrado_usd),
    porCobrar: sumar(items, (i) => i.por_cobrar, (i) => i.por_cobrar_usd),
  };
}

/** "$ 300.000,00 + USD 30,00": las monedas con algo; sin nada, pesos en cero. */
export function textoTotales(t: TotalFees): string {
  const partes = MONEDAS.filter((m) => aCentavos(t[m]) !== 0n).map((m) => formatMoneda(t[m], m));
  return partes.length ? partes.join(" + ") : formatMoneda(0, "ARS");
}

/** Debajo del estado: con qué factura se cobró el A (o cuánto falta si se cobró una parte) y a quién se acreditó el B. */
export function textoEstadoFee(i: FeeGenerado): string | null {
  if (i.tipo === "B") return i.acreditado_a.length ? i.acreditado_a.map((a) => `${a.nombre} ${formatMoneda(a.monto, i.moneda)}`).join(" · ") : null;
  if (i.estado === "por_cobrar") {
    return aCentavos(i.cobrado) > 0n ? `Cobrado ${formatMoneda(i.cobrado, i.moneda)} · falta ${formatMoneda(i.por_cobrar, i.moneda)}` : null;
  }
  return i.liquidaciones.length ? i.liquidaciones.map((l) => `Factura ${l.factura_nro} · ${formatFecha(l.fecha)}`).join(" · ") : null;
}

export const liquidable = (i: FeeGenerado) => i.tipo === "A" && i.estado === "por_cobrar";

/** Se eligen fees A por cobrar, todos de la misma moneda (una liquidación va en una sola). */
export function puedeElegir(i: FeeGenerado, seleccion: number[], items: FeeGenerado[]): boolean {
  if (!liquidable(i)) return false;
  const primero = items.find((x) => seleccion.includes(x.fee_id));
  return !primero || primero.moneda === i.moneda;
}

export function alternarSeleccion(seleccion: number[], feeId: number): number[] {
  return seleccion.includes(feeId) ? seleccion.filter((id) => id !== feeId) : [...seleccion, feeId];
}

export function totalPorCobrar(items: FeeGenerado[]): string {
  return deCentavos(items.reduce((t, i) => t + aCentavos(i.por_cobrar), 0n));
}

// ------------------------------------------------------------------ quiénes reciben el fee B

export interface LineaBeneficiario {
  fiduciante_id: number;
  nombre: string;
  /** Texto como lo escribe el admin ("50", "33,3334"). */
  porcentaje: string;
}

export function formBeneficiarios(beneficiarios: BeneficiarioFeeB[]): LineaBeneficiario[] {
  return beneficiarios.map((b) => ({ fiduciante_id: b.fiduciante_id, nombre: b.nombre, porcentaje: aEditable(aEscalado(b.porcentaje) ?? 0) }));
}

export { partesIguales };

export function agregarBeneficiario(lineas: LineaBeneficiario[], fiduciante: { id: number; nombre: string }): LineaBeneficiario[] {
  if (lineas.some((l) => l.fiduciante_id === fiduciante.id)) return lineas;
  return partesIguales([...lineas, { fiduciante_id: fiduciante.id, nombre: fiduciante.nombre, porcentaje: "" }]);
}

export function quitarBeneficiario(lineas: LineaBeneficiario[], fiduciante_id: number): LineaBeneficiario[] {
  return partesIguales(lineas.filter((l) => l.fiduciante_id !== fiduciante_id));
}

export function opcionesBeneficiario(fiduciantes: { id: number; nombre: string }[], lineas: LineaBeneficiario[]): OpcionSelect[] {
  return fiduciantes.filter((f) => !lineas.some((l) => l.fiduciante_id === f.id)).map((f) => ({ valor: String(f.id), etiqueta: f.nombre }));
}

/** null si está bien (vacía también: el fee B queda sin acreditar). */
export function validarBeneficiarios(lineas: LineaBeneficiario[]): string | null {
  if (lineas.length === 0) return null;
  let total = 0;
  for (const l of lineas) {
    const e = aEscalado(l.porcentaje);
    if (e === null || e === 0 || e > CIEN) return `El % de ${l.nombre} tiene que ser mayor que 0 y hasta 100`;
    total += e;
  }
  if (lineas.length === 1 && total !== CIEN) return "Si lo recibe uno solo, es el 100 %";
  if (total !== CIEN) return `Los % del fee B suman ${formatEscalado(total)} %: tienen que sumar 100`;
  return null;
}

export function aPayloadBeneficiarios(lineas: LineaBeneficiario[]): PayloadBeneficiarios {
  return { beneficiarios: lineas.map((l) => ({ fiduciante_id: l.fiduciante_id, porcentaje: aTextoApi(aEscalado(l.porcentaje) ?? 0) })) };
}

/** ¿Cambió algo respecto de lo guardado? (para habilitar Guardar). */
export function beneficiariosCambiaron(lineas: LineaBeneficiario[], guardados: BeneficiarioFeeB[]): boolean {
  if (lineas.length !== guardados.length) return true;
  return lineas.some((l, i) => {
    const g = guardados[i];
    return !g || l.fiduciante_id !== g.fiduciante_id || aEscalado(l.porcentaje) !== aEscalado(g.porcentaje);
  });
}

// ------------------------------------------------------------------ liquidación de los fees A elegidos

export interface FormLiquidacion {
  /** La de los fees elegidos. */
  moneda: Moneda;
  factura_nro: string;
  fecha: string;
  /** id de la caja como texto; "" = la principal (no se manda). */
  caja_id: string;
  tipo_cambio: string;
}

export type ErroresLiquidacion = Partial<Record<"factura_nro" | "fecha" | "tipo_cambio", string>>;

export function formLiquidacion(moneda: Moneda, tcSugerido: string | null | undefined, cajaDefecto = "", hoy = hoyISO()): FormLiquidacion {
  return { moneda, factura_nro: "", fecha: hoy, caja_id: cajaDefecto, tipo_cambio: tipoCambioAInput(tcSugerido) };
}

export function validarLiquidacion(f: FormLiquidacion): ErroresLiquidacion {
  const e: ErroresLiquidacion = {};
  const factura = f.factura_nro.trim();
  if (!factura) e.factura_nro = "Poné el número de la factura";
  else if (factura.length > LARGO_FACTURA) e.factura_nro = `Hasta ${LARGO_FACTURA} caracteres`;
  if (!f.fecha) e.fecha = "Elegí la fecha";
  if (f.moneda === "ARS" && parseTipoCambio(f.tipo_cambio) === null) e.tipo_cambio = "Una liquidación en pesos necesita el tipo de cambio";
  return e;
}

export function aPayloadLiquidacion(f: FormLiquidacion, feeIds: number[]): PayloadLiquidacion {
  return {
    fee_ids: feeIds, factura_nro: f.factura_nro.trim(), fecha: f.fecha,
    tipo_cambio: f.moneda === "ARS" ? parseTipoCambio(f.tipo_cambio) : null,
    ...(f.caja_id ? { caja_id: Number(f.caja_id) } : {}),
  };
}
